import { IMAGE_COMPRESSION } from "../config/constants";

/**
 * Image compression utility
 * Compresses image files before upload to reduce bandwidth and storage
 *
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width (default: from constants)
 * @param {number} options.maxHeight - Maximum height (default: from constants)
 * @param {number} options.quality - JPEG quality 0-1 (default: from constants)
 * @param {string} options.mimeType - Output format (default: from constants)
 * @returns {Promise<File>} Compressed image file
 */
export async function compressImage(file, options = {}) {
  const {
    maxWidth = IMAGE_COMPRESSION.MAX_WIDTH,
    maxHeight = IMAGE_COMPRESSION.MAX_HEIGHT,
    quality = IMAGE_COMPRESSION.QUALITY,
    mimeType = IMAGE_COMPRESSION.MIME_TYPE,
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      // Calculate resize ratio (never upscale)
      const ratio = Math.min(maxWidth / img.width, maxHeight / img.height, 1);

      const canvas = document.createElement("canvas");
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            return reject(new Error("Compression failed"));
          }

          const compressedFile = new File(
            [blob],
            file.name.replace(/\.\w+$/, ".jpg"),
            {
              type: mimeType,
              lastModified: Date.now(),
            }
          );

          resolve(compressedFile);
        },
        mimeType,
        quality
      );

      URL.revokeObjectURL(url);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
}
